/**
 * Robot Details ▸ Operations: dispatch or patrol this robot.
 * Both rows navigate away rather than embedding a form — each owns its own
 * full-screen layout, so squeezing it into a tab panel would just unpick that.
 */
import PropTypes from "prop-types";
import { Box, Stack, Typography, Button } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RouteIcon from "@mui/icons-material/Route";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { tokens } from "../../theme";

/** One operation row: label + description on the left, an action button on the right. */
const OperationRow = ({ title, description, icon, label, onClick, disabled }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "stretch", sm: "center" }}
    gap={1.5}
    sx={{ p: "14px 16px" }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: tokens.text2,
          mb: 0.375,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: tokens.text3 }}>
        {description}
        <Box component="span" sx={{ color: tokens.text3, fontStyle: "italic" }}>
          {" "}
          Opens in a separate page.
        </Box>
      </Typography>
    </Box>
    <Button
      size="small"
      variant="outlined"
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      sx={{
        flexShrink: 0,
        borderColor: tokens.accentSoft,
        color: tokens.accent,
        background: tokens.accentSoft,
        "&:hover": { background: tokens.accentSoft, borderColor: tokens.accent },
      }}
    >
      {label}
    </Button>
  </Stack>
);

OperationRow.propTypes = {
  title: PropTypes.string,
  description: PropTypes.node,
  icon: PropTypes.node,
  label: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

const RobotOperationsTab = ({ robot, onDispatch, onPatrol, onOpenLightDialog }) => {
  // Gates both rows: serialID is what the command endpoints and socket rooms
  // key on (not the Mongo id), so its absence is the honest reason neither
  // action can be sent to this robot yet.
  const ready = Boolean(robot?.serialID);

  return (
    <Box sx={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 2 }}>
      {!ready && (
        <Box
          sx={{
            m: 2,
            mb: 0,
            p: 1.25,
            borderRadius: "10px",
            background: tokens.warningSoft,
            border: `1px solid ${tokens.warning}30`,
            color: tokens.text2,
            fontSize: 12.5,
          }}
        >
          This robot has no serial ID, so it cannot receive commands. Contact an administrator to
          have one assigned.
        </Box>
      )}

      <OperationRow
        title="Dispatch"
        description="Send this robot to a destination and follow it on the live map."
        icon={<SendIcon sx={{ fontSize: "15px !important" }} />}
        label="Dispatch robot"
        onClick={onDispatch}
        disabled={!ready}
      />

      <Box sx={{ borderTop: `1px solid ${tokens.border}` }} />

      <OperationRow
        title="Patrol Mode"
        description="Run this robot on a repeating waypoint loop with a configurable wait time and round count."
        icon={<RouteIcon sx={{ fontSize: "15px !important" }} />}
        label="Set up patrol"
        onClick={onPatrol}
        disabled={!ready}
      />

      <Box sx={{ borderTop: `1px solid ${tokens.border}` }} />

      <OperationRow
        title="Light Control"
        description="Set this robot's LED colour, effect and speed."
        icon={<LightbulbIcon sx={{ fontSize: "15px !important" }} />}
        label="Open light control"
        onClick={onOpenLightDialog}
        disabled={!ready}
      />
    </Box>
  );
};

RobotOperationsTab.propTypes = {
  robot: PropTypes.object,
  onDispatch: PropTypes.func.isRequired,
  onPatrol: PropTypes.func.isRequired,
  onOpenLightDialog: PropTypes.func.isRequired,
};

export default RobotOperationsTab;
